// Тексты новых страниц (маршруты, тимбилдинг) и метаданные всех страниц.
// Тексты главной по-прежнему лежат в locales/*.json.
// ВАЖНО: публиковать только подтверждённые условия. Цена, длительность, языки,
// количество точек и условия возврата взяты с текущего сайта и из каталога квестов.

import type { Lang, PageId, QuestId } from './site';

interface Meta {
  title: string;
  description: string;
}

export interface QuestCopy {
  city: string;
  h1: string;
  lead: string;
  intro: string[];
  highlightsTitle: string;
  highlights: string[];
  paidNote: string;
  difficulty: string;
  startAddress: string;
}

export interface Copy {
  meta: Record<PageId | 'notFound', Meta>;
  ui: {
    home: string;
    nav: string;
    buy: string;
    haveAccess: string;
    details: string;
    startOnMap: string;
    otherRoutes: string;
    factsTitle: string;
    facts: {
      format: string;
      formatValue: string;
      price: string;
      priceValue: string;
      duration: string;
      durationValue: string;
      locations: string;
      locationsValue: (n: number) => string;
      distance: string;
      distanceValue: (km: number) => string;
      difficulty: string;
      age: string;
      start: string;
      languages: string;
      languagesValue: string;
      terrain: string;
      terrainValue: string;
    };
    howTitle: string;
    howSteps: string[];
    needTitle: string;
    needItems: string[];
    faqTitle: string;
    faq: { q: string; a: string }[];
    teamLink: string;
    teamLinkText: string;
    notFoundText: string;
    notFoundCta: string;
  };
  quests: Record<QuestId, QuestCopy>;
  team: {
    h1: string;
    lead: string;
    sections: { title: string; text?: string; items?: string[] }[];
    orderTitle: string;
    orderText: string;
    orderItems: string[];
    whatsappCta: string;
    emailCta: string;
    whatsappText: string;
    emailSubject: string;
    routesTitle: string;
    /** Строка про счёт на компанию: текст до ссылки, слово-ссылка, текст после. */
    invoiceBefore: string;
    invoiceLink: string;
    invoiceAfter: string;
    invoiceWhatsappText: string;
  };
}

const en: Copy = {
  meta: {
    home: {
      title: 'Algarve Quest — Self-Guided City Quests in Faro, Olhão & Alte',
      description:
        'Explore Faro, Olhão and Alte through self-guided city quests on your phone. Discover local stories, solve puzzles and choose your route. €12.50 per person.',
    },
    faro: {
      title: 'Faro Self-Guided City Quest — Mysteries of Old Faro | Algarve Quest',
      description:
        'A self-guided quest through the walled Old Town of Faro: 16 locations, puzzles and local history on your phone. About 1.5 hours, €12.50 per person, ages 12+.',
    },
    olhao: {
      title: 'Olhão Self-Guided City Quest — Legends of Olhão | Algarve Quest',
      description:
        'A self-guided quest through Olhão: 13 locations, fishermen legends, the old market and the “city of cubes”. On your phone, about 1.5 hours, €12.50 per person.',
    },
    alte: {
      title: 'Alte Self-Guided Village Quest — Secrets of Alte | Algarve Quest',
      description:
        'A self-guided quest in the hill village of Alte: 16 locations, springs, legends and open-air art. On your phone, about 1.5 hours, €12.50 per person, ages 7+.',
    },
    team: {
      title: 'Team Building Quests in the Algarve — Faro, Olhão & Alte | Algarve Quest',
      description:
        'Team building in the Algarve: teams play a self-guided city quest on their phones in Faro, Olhão or Alte and compete on points and time. Contact us to plan your event.',
    },
    notFound: { title: 'Page not found | Algarve Quest', description: 'This page does not exist.' },
  },
  ui: {
    home: 'Home',
    nav: 'Team building',
    buy: 'Buy this quest',
    haveAccess: 'Already bought? Open my quest',
    details: 'Route details',
    startOnMap: 'Open the start point on Google Maps',
    otherRoutes: 'Other quest routes',
    factsTitle: 'Key facts',
    facts: {
      format: 'Format',
      formatValue: 'Self-guided quest in your phone’s browser — no guide, no app to install',
      price: 'Price',
      priceValue: '€12.50 per person, children under 12 free',
      duration: 'Duration',
      durationValue: 'About 1.5 hours, no time limit — pause and continue later',
      locations: 'Locations',
      locationsValue: (n) => `${n} locations`,
      distance: 'Route length',
      distanceValue: (km) => `about ${km.toFixed(1)} km on foot`,
      difficulty: 'Difficulty',
      age: 'Recommended age',
      start: 'Start point',
      languages: 'Quest languages',
      languagesValue: 'English, Portuguese, Russian',
      terrain: 'Terrain',
      terrainValue: 'Cobblestones and stairs along the route',
    },
    howTitle: 'How to buy and play',
    howSteps: [
      'Buy the quest online. After payment you receive a personal quest link — no booking or fixed time.',
      'Go to the start point whenever you like. We recommend playing in daylight: some clues rely on small details.',
      'At each location read a short story, solve a puzzle and earn points. Built-in hints help if you get stuck.',
      'The link stays active until you complete the final challenge, so you can take a break and resume hours or days later.',
    ],
    needTitle: 'What you need',
    needItems: [
      'A charged phone or tablet with mobile internet',
      'Comfortable shoes',
      'Daylight and a bit of curiosity',
    ],
    faqTitle: 'Questions about this route',
    faq: [
      {
        q: 'Can children play?',
        a: 'Yes. Children under 12 play for free. Check the recommended age for this route above.',
      },
      {
        q: 'Do I need to book a time?',
        a: 'No. Buy the quest and start whenever you want — today, tomorrow or later.',
      },
      {
        q: 'What if some places are closed?',
        a: 'Some locations, such as museums, churches or restaurants, have their own opening hours or an entrance fee. You can skip them and continue the quest.',
      },
      {
        q: 'Can I get a refund?',
        a: 'Yes — if you have not started the quest and contact us within 7 days of purchase. We also refund if more than two locations on the route are not accessible (excluding places with their own opening hours) — just send us a photo.',
      },
      {
        q: 'What if something does not work?',
        a: 'Write to us on WhatsApp (+351 911 540 083, messages only) or by email at algarvequest@gmail.com.',
      },
    ],
    teamLink: 'Planning a group event?',
    teamLinkText: 'Team building quests',
    notFoundText: 'Sorry, this page does not exist.',
    notFoundCta: 'Go to the home page',
  },
  quests: {
    faro: {
      city: 'Faro',
      h1: 'Faro self-guided city quest: Mysteries of Old Faro',
      lead: 'A walking quest through the walled Old Town of Faro, the capital of the Algarve. You play on your phone at your own pace.',
      intro: [
        'The route starts at the big FARO letters by the marina and leads through the Arco da Vila into the old town inside the walls. At every stop you read a short story about the place and solve a puzzle hidden in what you see around you.',
        'Along the way you meet the many layers of Faro’s past — from the ancient port of Ossónoba to the bishops and kings whose monuments still stand in the old town.',
      ],
      highlightsTitle: 'Places on the route include',
      highlights: [
        'The FARO letters at the marina',
        'Arco da Vila, the main gate of the old town',
        'Faro Cathedral and its bell tower',
        'Monument to King Afonso III',
        'Municipal Museum',
        'Arco do Repouso',
      ],
      paidNote:
        'The cathedral, the bell tower, the museum and one restaurant stop have their own entrance fees or opening hours. They are optional — you can skip them and still finish the quest.',
      difficulty: 'Medium',
      startAddress: 'The FARO letters, Marina de Faro, Rua Comandante Francisco Manuel',
    },
    olhao: {
      city: 'Olhão',
      h1: 'Olhão self-guided city quest: Legends of Olhão',
      lead: 'A walking quest through Olhão, a fishing town on the Ria Formosa lagoon, built around its local legends. You play on your phone at your own pace.',
      intro: [
        'The route starts at the viewpoint with the travellers sculpture on the waterfront and winds through the white streets of the historic centre. At every stop you hear one of Olhão’s stories and solve a puzzle on the spot.',
        'This route is about legends: the enchanted Moor and his golden palace, the beautiful Floripes, the magic spring, smugglers and the fishermen who built the town.',
      ],
      highlightsTitle: 'Places and stories on the route include',
      highlights: [
        'The travellers sculpture viewpoint',
        'The boat Margareta',
        'Legends of Arraúl, the enchanted Moor and Floripes',
        'The old canning factory buildings',
        'The old market of Olhão',
        'An azulejo tile panel and the “city of cubes”',
      ],
      paidNote: 'All locations on this route are outdoors.',
      difficulty: 'Easy',
      startAddress: 'Viewpoint with the travellers sculpture, Av. 5 de Outubro, Olhão',
    },
    alte: {
      city: 'Alte',
      h1: 'Alte self-guided village quest: Secrets of Alte',
      lead: 'A walking quest through Alte, a traditional white village in the hills of the Serra do Caldeirão. You play on your phone at your own pace.',
      intro: [
        'The route starts at the Church of the Assumption and follows the village down to its springs. Alte is known for its white houses with carved chimneys, cobbled streets and wall paintings showing village life.',
        'At every stop you read a short story about the place and solve a puzzle — from the springs of the Alte river to the legend of how the village got its name.',
      ],
      highlightsTitle: 'Places on the route include',
      highlights: [
        'Church of Our Lady of the Assumption',
        'The springs of the Alte river (Fonte Pequena)',
        'Chapel of São Luís',
        'Museum centre of Cândido Guerreiro and the Counts of Alte',
        'House of Craftsmen and the open-air art gallery',
        'The legend of Alte’s name',
      ],
      paidNote:
        'Entry to the church and the museum may be paid or limited by opening hours. The puzzle at the church is outside, so you can solve it without going in.',
      difficulty: 'Easy',
      startAddress: 'Church of the Assumption, Rua Nova da Igreja, Alte',
    },
  },
  team: {
    h1: 'Team building quests in the Algarve',
    lead: 'Turn a city walk into a friendly competition. Teams play the same self-guided quest in Faro, Olhão or Alte on their phones and compete on points and time.',
    sections: [
      {
        title: 'How it works',
        items: [
          'Split your group into teams of 4–5 people. Each team plays on its own phone.',
          'All teams follow the same route and solve puzzles at each location.',
          'Points: 10 for a correct answer on the first try, 5 if a hint was used, 0 after two mistakes.',
          'At the finish, compare scores and time — the team with the most points and the fastest time wins.',
        ],
      },
      {
        title: 'Where',
        text: 'Choose one of three routes: the walled Old Town of Faro, the waterfront town of Olhão or the hill village of Alte. Each route takes about 1.5 hours at a relaxed pace.',
      },
      {
        title: 'What participants need',
        items: [
          'One charged phone with mobile internet per team',
          'Comfortable shoes — routes include cobblestones and stairs',
          'Daylight: some clues rely on small details',
        ],
      },
    ],
    orderTitle: 'Plan your event',
    orderText: 'Write to us and we will confirm the conditions for your group. Please include:',
    orderItems: ['Preferred city or route', 'Approximate date', 'Number of participants'],
    whatsappCta: 'Write on WhatsApp',
    emailCta: 'Send an email',
    whatsappText: 'Hello! We would like to organise a team building quest.',
    emailSubject: 'Team building quest',
    routesTitle: 'Routes available for teams',
    invoiceBefore: 'Need an invoice for your company? Message us on ',
    invoiceLink: 'WhatsApp',
    invoiceAfter: ' and we will issue one.',
    invoiceWhatsappText: 'Hello! We need an invoice for our company for the team building.',
  },
};

const pt: Copy = {
  meta: {
    home: {
      title: 'Algarve Quest — Percursos-jogo autoguiados em Faro, Olhão e Alte',
      description:
        'Explora Faro, Olhão e Alte com percursos-jogo autoguiados no telemóvel. Descobre histórias locais, resolve enigmas e escolhe o teu percurso. €12,50 por pessoa.',
    },
    faro: {
      title: 'Percurso-jogo autoguiado em Faro — Mistérios do Velho Faro | Algarve Quest',
      description:
        'Um percurso-jogo autoguiado pela Vila Adentro de Faro: 16 locais, enigmas e história local no telemóvel. Cerca de 1,5 horas, €12,50 por pessoa, a partir dos 12 anos.',
    },
    olhao: {
      title: 'Percurso-jogo autoguiado em Olhão — Lendas de Olhão | Algarve Quest',
      description:
        'Um percurso-jogo autoguiado por Olhão: 13 locais, lendas de pescadores, o mercado velho e a “cidade cubista”. No telemóvel, cerca de 1,5 horas, €12,50 por pessoa.',
    },
    alte: {
      title: 'Percurso-jogo autoguiado em Alte — Segredos de Alte | Algarve Quest',
      description:
        'Um percurso-jogo autoguiado na aldeia de Alte: 16 locais, fontes, lendas e arte ao ar livre. No telemóvel, cerca de 1,5 horas, €12,50 por pessoa, a partir dos 7 anos.',
    },
    team: {
      title: 'Team building no Algarve — percursos-jogo em Faro, Olhão e Alte | Algarve Quest',
      description:
        'Team building no Algarve: as equipas jogam um percurso-jogo autoguiado no telemóvel em Faro, Olhão ou Alte e competem por pontos e tempo. Contacta-nos para organizar.',
    },
    notFound: { title: 'Página não encontrada | Algarve Quest', description: 'Esta página não existe.' },
  },
  ui: {
    home: 'Início',
    nav: 'Team building',
    buy: 'Comprar este percurso',
    haveAccess: 'Já comprou? Abrir o meu percurso',
    details: 'Detalhes do percurso',
    startOnMap: 'Abrir o ponto de partida no Google Maps',
    otherRoutes: 'Outros percursos',
    factsTitle: 'Informação essencial',
    facts: {
      format: 'Formato',
      formatValue: 'Percurso-jogo autoguiado no navegador do telemóvel — sem guia e sem instalar aplicações',
      price: 'Preço',
      priceValue: '€12,50 por pessoa, crianças até 12 anos grátis',
      duration: 'Duração',
      durationValue: 'Cerca de 1,5 horas, sem limite de tempo — podes fazer pausas e continuar depois',
      locations: 'Locais',
      locationsValue: (n) => `${n} locais`,
      distance: 'Extensão',
      distanceValue: (km) => `cerca de ${km.toFixed(1).replace('.', ',')} km a pé`,
      difficulty: 'Dificuldade',
      age: 'Idade recomendada',
      start: 'Ponto de partida',
      languages: 'Idiomas do percurso',
      languagesValue: 'Inglês, português, russo',
      terrain: 'Terreno',
      terrainValue: 'O percurso inclui calçada e escadas',
    },
    howTitle: 'Como comprar e jogar',
    howSteps: [
      'Compra o percurso online. Depois do pagamento recebes um link pessoal — sem reservas nem horário fixo.',
      'Vai ao ponto de partida quando quiseres. Recomendamos jogar de dia: algumas pistas dependem de pequenos detalhes.',
      'Em cada local lês uma pequena história, resolves um enigma e ganhas pontos. Se ficares preso, há dicas.',
      'O link fica ativo até concluíres o último desafio, por isso podes fazer uma pausa e continuar horas ou dias depois.',
    ],
    needTitle: 'O que precisas',
    needItems: [
      'Um telemóvel ou tablet carregado com internet móvel',
      'Calçado confortável',
      'Luz do dia e um pouco de curiosidade',
    ],
    faqTitle: 'Perguntas sobre este percurso',
    faq: [
      {
        q: 'As crianças podem jogar?',
        a: 'Sim. Crianças até 12 anos jogam grátis. Vê acima a idade recomendada para este percurso.',
      },
      {
        q: 'É preciso reservar hora?',
        a: 'Não. Compra o percurso e começa quando quiseres — hoje, amanhã ou mais tarde.',
      },
      {
        q: 'E se alguns locais estiverem fechados?',
        a: 'Alguns locais, como museus, igrejas ou restaurantes, têm horário próprio ou entrada paga. Podes saltá-los e continuar o percurso.',
      },
      {
        q: 'Posso pedir reembolso?',
        a: 'Sim — se ainda não começaste o percurso e nos contactares até 7 dias após a compra. Também devolvemos o valor se mais de dois locais estiverem inacessíveis (excluindo locais com horário próprio) — basta enviares uma foto.',
      },
      {
        q: 'E se algo não funcionar?',
        a: 'Escreve-nos no WhatsApp (+351 911 540 083, apenas mensagens) ou por email para algarvequest@gmail.com.',
      },
    ],
    teamLink: 'Estás a organizar um evento de grupo?',
    teamLinkText: 'Team building',
    notFoundText: 'Desculpa, esta página não existe.',
    notFoundCta: 'Ir para a página inicial',
  },
  quests: {
    faro: {
      city: 'Faro',
      h1: 'Percurso-jogo autoguiado em Faro: Mistérios do Velho Faro',
      lead: 'Um percurso-jogo a pé pela Vila Adentro, a cidade velha muralhada de Faro, capital do Algarve. Jogas no telemóvel, ao teu ritmo.',
      intro: [
        'O percurso começa no letreiro FARO junto à marina e entra pelo Arco da Vila na cidade velha dentro das muralhas. Em cada paragem lês uma pequena história sobre o local e resolves um enigma escondido no que vês à tua volta.',
        'Pelo caminho descobres as várias camadas do passado de Faro — desde o antigo porto de Ossónoba até aos bispos e reis cujos monumentos ainda estão na cidade velha.',
      ],
      highlightsTitle: 'Alguns locais do percurso',
      highlights: [
        'O letreiro FARO na marina',
        'Arco da Vila, a porta principal da cidade velha',
        'A Sé de Faro e o seu campanário',
        'Monumento a D. Afonso III',
        'Museu Municipal',
        'Arco do Repouso',
      ],
      paidNote:
        'A Sé, o campanário, o museu e uma paragem num restaurante têm entrada paga ou horário próprio. São opcionais — podes saltá-los e terminar o percurso.',
      difficulty: 'Médio',
      startAddress: 'Letreiro «Faro», Marina de Faro, Rua Comandante Francisco Manuel',
    },
    olhao: {
      city: 'Olhão',
      h1: 'Percurso-jogo autoguiado em Olhão: Lendas de Olhão',
      lead: 'Um percurso-jogo a pé por Olhão, vila piscatória na Ria Formosa, construído à volta das suas lendas. Jogas no telemóvel, ao teu ritmo.',
      intro: [
        'O percurso começa no miradouro com a composição escultórica dos viajantes, junto à ria, e segue pelas ruas brancas do centro histórico. Em cada paragem ouves uma das histórias de Olhão e resolves um enigma no local.',
        'Este percurso é sobre lendas: o mouro encantado e o palácio dourado, a bela Floripes, a fonte mágica, os contrabandistas e os pescadores que construíram a vila.',
      ],
      highlightsTitle: 'Alguns locais e histórias do percurso',
      highlights: [
        'O miradouro com a composição escultórica dos viajantes',
        'O barco Margareta',
        'As lendas de Arraúl, do mouro encantado e de Floripes',
        'A fábrica velha de conservas',
        'O mercado velho de Olhão',
        'Um painel de azulejos e a “cidade cubista”',
      ],
      paidNote: 'Todos os locais deste percurso são ao ar livre.',
      difficulty: 'Fácil',
      startAddress: 'Miradouro com a composição escultórica dos viajantes, Av. 5 de Outubro, Olhão',
    },
    alte: {
      city: 'Alte',
      h1: 'Percurso-jogo autoguiado em Alte: Segredos de Alte',
      lead: 'Um percurso-jogo a pé por Alte, aldeia branca tradicional na Serra do Caldeirão. Jogas no telemóvel, ao teu ritmo.',
      intro: [
        'O percurso começa na Igreja de Nossa Senhora da Assunção e desce pela aldeia até às fontes. Alte é conhecida pelas casas brancas com chaminés rendilhadas, ruas de calçada e murais com cenas da vida da aldeia.',
        'Em cada paragem lês uma pequena história sobre o local e resolves um enigma — desde as fontes do rio Alte até à lenda sobre a origem do nome da aldeia.',
      ],
      highlightsTitle: 'Alguns locais do percurso',
      highlights: [
        'Igreja de Nossa Senhora da Assunção',
        'As fontes do rio Alte (Fonte Pequena)',
        'Capela de São Luís',
        'Pólo Museológico Cândido Guerreiro e Condes de Alte',
        'Casa dos Artesãos e a galeria de arte ao ar livre',
        'A lenda da origem do nome de Alte',
      ],
      paidNote:
        'A entrada na igreja e no museu pode ser paga ou depender do horário. O enigma da igreja está no exterior, por isso podes resolvê-lo sem entrar.',
      difficulty: 'Fácil',
      startAddress: 'Igreja de Nossa Senhora da Assunção, Rua Nova da Igreja, Alte',
    },
  },
  team: {
    h1: 'Team building no Algarve com percursos-jogo',
    lead: 'Transforma um passeio pela cidade numa competição amigável. As equipas jogam o mesmo percurso-jogo autoguiado em Faro, Olhão ou Alte no telemóvel e competem por pontos e tempo.',
    sections: [
      {
        title: 'Como funciona',
        items: [
          'Divide o grupo em equipas de 4–5 pessoas. Cada equipa joga no seu telemóvel.',
          'Todas as equipas fazem o mesmo percurso e resolvem enigmas em cada local.',
          'Pontos: 10 por resposta certa à primeira, 5 se usaram uma dica, 0 depois de dois erros.',
          'No final comparam-se pontos e tempo — ganha a equipa com mais pontos e o melhor tempo.',
        ],
      },
      {
        title: 'Onde',
        text: 'Escolhe um de três percursos: a cidade velha de Faro, a vila ribeirinha de Olhão ou a aldeia serrana de Alte. Cada percurso demora cerca de 1,5 horas a um ritmo tranquilo.',
      },
      {
        title: 'O que os participantes precisam',
        items: [
          'Um telemóvel carregado com internet móvel por equipa',
          'Calçado confortável — os percursos incluem calçada e escadas',
          'Luz do dia: algumas pistas dependem de pequenos detalhes',
        ],
      },
    ],
    orderTitle: 'Organiza o teu evento',
    orderText: 'Escreve-nos e confirmamos as condições para o teu grupo. Indica:',
    orderItems: ['Cidade ou percurso preferido', 'Data aproximada', 'Número de participantes'],
    whatsappCta: 'Escrever no WhatsApp',
    emailCta: 'Enviar email',
    whatsappText: 'Olá! Gostaríamos de organizar um team building com um percurso-jogo.',
    emailSubject: 'Team building — percurso-jogo',
    routesTitle: 'Percursos disponíveis para equipas',
    invoiceBefore: 'Precisas de fatura em nome da empresa? Escreve-nos no ',
    invoiceLink: 'WhatsApp',
    invoiceAfter: ' e emitimos a fatura.',
    invoiceWhatsappText: 'Olá! Precisamos de uma fatura em nome da empresa para o team building.',
  },
};

const ru: Copy = {
  meta: {
    home: {
      title: 'Algarve Quest — квесты-прогулки без гида в Фаро, Оляо и Алте',
      description:
        'Самостоятельные квесты-прогулки по Фаро, Оляо и Алте на телефоне: истории, легенды и загадки. Выбирай маршрут и иди в своём темпе. €12,50 с человека.',
    },
    faro: {
      title: 'Квест-прогулка по Фаро без гида — Тайны Старого Фаро | Algarve Quest',
      description:
        'Самостоятельный квест по старому городу Фаро внутри крепостных стен: 16 локаций, загадки и история на телефоне. Около 1,5 часа, €12,50 с человека, 12+.',
    },
    olhao: {
      title: 'Квест-прогулка по Оляо без гида — Легенды Оляо | Algarve Quest',
      description:
        'Самостоятельный квест по Оляо: 13 локаций, легенды рыбаков, старый рынок и «город кубов». На телефоне, около 1,5 часа, €12,50 с человека.',
    },
    alte: {
      title: 'Квест-прогулка по Алте без гида — Секреты Алте | Algarve Quest',
      description:
        'Самостоятельный квест по горной деревне Алте: 16 локаций, источники, легенды и уличное искусство. На телефоне, около 1,5 часа, €12,50 с человека, 7+.',
    },
    team: {
      title: 'Тимбилдинг в Алгарве — командные квесты в Фаро, Оляо и Алте | Algarve Quest',
      description:
        'Тимбилдинг в Алгарве: команды проходят один и тот же квест-прогулку на телефонах в Фаро, Оляо или Алте и соревнуются по очкам и времени. Напишите нам.',
    },
    notFound: { title: 'Страница не найдена | Algarve Quest', description: 'Такой страницы нет.' },
  },
  ui: {
    home: 'Главная',
    nav: 'Тимбилдинг',
    buy: 'Купить этот квест',
    haveAccess: 'Уже купили? Открыть мой квест',
    details: 'Подробнее о маршруте',
    startOnMap: 'Открыть точку старта в Google Maps',
    otherRoutes: 'Другие маршруты',
    factsTitle: 'Коротко о маршруте',
    facts: {
      format: 'Формат',
      formatValue: 'Самостоятельный квест в браузере телефона — без гида и без установки приложений',
      price: 'Цена',
      priceValue: '€12,50 с человека, дети до 12 лет — бесплатно',
      duration: 'Длительность',
      durationValue: 'Около 1,5 часа, без ограничения по времени — можно прерваться и продолжить позже',
      locations: 'Локации',
      locationsValue: (n) => `${n} локаций`,
      distance: 'Протяжённость',
      distanceValue: (km) => `около ${km.toFixed(1).replace('.', ',')} км пешком`,
      difficulty: 'Сложность',
      age: 'Рекомендуемый возраст',
      start: 'Точка старта',
      languages: 'Языки квеста',
      languagesValue: 'Английский, португальский, русский',
      terrain: 'Покрытие',
      terrainValue: 'На маршруте брусчатка и лестницы',
    },
    howTitle: 'Как купить и пройти',
    howSteps: [
      'Купи квест онлайн. После оплаты придёт личная ссылка — без бронирования и фиксированного времени.',
      'Приходи к точке старта когда удобно. Лучше играть днём: некоторые подсказки основаны на мелких деталях.',
      'На каждой локации — короткая история, загадка и очки. Если застрял, помогут встроенные подсказки.',
      'Ссылка активна, пока не пройдёшь последнее задание, поэтому можно сделать перерыв и продолжить через несколько часов или дней.',
    ],
    needTitle: 'Что понадобится',
    needItems: [
      'Заряженный телефон или планшет с мобильным интернетом',
      'Удобная обувь',
      'Дневной свет и немного любопытства',
    ],
    faqTitle: 'Вопросы об этом маршруте',
    faq: [
      {
        q: 'Можно ли с детьми?',
        a: 'Да. Дети до 12 лет играют бесплатно. Рекомендуемый возраст для этого маршрута указан выше.',
      },
      {
        q: 'Нужно ли бронировать время?',
        a: 'Нет. Покупаешь квест и начинаешь когда хочешь — сегодня, завтра или позже.',
      },
      {
        q: 'А если какие-то места закрыты?',
        a: 'У некоторых локаций — музеев, церквей, ресторанов — свой режим работы или платный вход. Их можно пропустить и продолжить квест.',
      },
      {
        q: 'Можно ли вернуть деньги?',
        a: 'Да — если ты ещё не начал квест и написал нам в течение 7 дней после покупки. Также вернём деньги, если больше двух локаций на маршруте недоступны (не считая мест со своим режимом работы) — просто пришли фото.',
      },
      {
        q: 'Что делать, если что-то не работает?',
        a: 'Напиши нам в WhatsApp (+351 911 540 083, только сообщения) или на почту algarvequest@gmail.com.',
      },
    ],
    teamLink: 'Планируете групповое мероприятие?',
    teamLinkText: 'Тимбилдинг-квесты',
    notFoundText: 'Извините, такой страницы нет.',
    notFoundCta: 'На главную',
  },
  quests: {
    faro: {
      city: 'Фаро',
      h1: 'Квест-прогулка по Фаро без гида: Тайны Старого Фаро',
      lead: 'Пешеходный квест по старому городу Фаро внутри крепостных стен — столице Алгарве. Играешь на телефоне в своём темпе.',
      intro: [
        'Маршрут начинается у больших букв FARO на набережной марины и через ворота Арку-да-Вила ведёт в старый город. На каждой остановке — короткая история о месте и загадка, ответ на которую нужно найти вокруг себя.',
        'По пути открываются разные слои прошлого Фаро — от древнего порта Оссонобы до епископов и королей, чьи памятники до сих пор стоят в старом городе.',
      ],
      highlightsTitle: 'Среди мест на маршруте',
      highlights: [
        'Буквы FARO у марины',
        'Арку-да-Вила — главные ворота старого города',
        'Кафедральный собор Фаро и его колокольня',
        'Памятник королю Афонсу III',
        'Муниципальный музей',
        'Арку-ду-Репозу',
      ],
      paidNote:
        'Вход в собор, на колокольню, в музей и одна остановка в ресторане — платные или зависят от часов работы. Они необязательны: их можно пропустить и всё равно пройти квест.',
      difficulty: 'Средняя',
      startAddress: 'Буквы FARO, Marina de Faro, Rua Comandante Francisco Manuel',
    },
    olhao: {
      city: 'Оляо',
      h1: 'Квест-прогулка по Оляо без гида: Легенды Оляо',
      lead: 'Пешеходный квест по Оляо — рыбацкому городку на лагуне Риа-Формоза, построенный вокруг местных легенд. Играешь на телефоне в своём темпе.',
      intro: [
        'Маршрут начинается на смотровой площадке со скульптурой путешественников у воды и петляет по белым улицам исторического центра. На каждой остановке — одна из историй Оляо и загадка прямо на месте.',
        'Этот маршрут — про легенды: зачарованный мавр и золотой дворец, прекрасная Флорипеш, волшебный источник, контрабандисты и рыбаки, которые построили город.',
      ],
      highlightsTitle: 'Среди мест и историй на маршруте',
      highlights: [
        'Смотровая площадка со скульптурой путешественников',
        'Лодка Margareta',
        'Легенды об Аррауле, зачарованном мавре и Флорипеш',
        'Здания старой консервной фабрики',
        'Старый рынок Оляо',
        'Панно из азулежу и «город кубов»',
      ],
      paidNote: 'Все локации этого маршрута — под открытым небом.',
      difficulty: 'Лёгкая',
      startAddress: 'Смотровая площадка со скульптурой путешественников, Av. 5 de Outubro, Olhão',
    },
    alte: {
      city: 'Алте',
      h1: 'Квест-прогулка по Алте без гида: Секреты Алте',
      lead: 'Пешеходный квест по Алте — традиционной белой деревне в горах Серра-ду-Калдейран. Играешь на телефоне в своём темпе.',
      intro: [
        'Маршрут начинается у церкви Успения и спускается через деревню к источникам. Алте известна белыми домами с резными дымоходами, мощёными улочками и росписями на стенах о жизни деревни.',
        'На каждой остановке — короткая история о месте и загадка: от источников реки Алте до легенды о том, откуда у деревни такое название.',
      ],
      highlightsTitle: 'Среди мест на маршруте',
      highlights: [
        'Церковь Успения Пресвятой Богородицы',
        'Источники реки Алте (Fonte Pequena)',
        'Часовня Святого Людовика',
        'Музейный центр Кандиду Геррейру и графов Алте',
        'Дом ремесленников и галерея под открытым небом',
        'Легенда о названии Алте',
      ],
      paidNote:
        'Вход в церковь и музей может быть платным или зависеть от часов работы. Загадка у церкви — снаружи, её можно решить, не заходя внутрь.',
      difficulty: 'Лёгкая',
      startAddress: 'Церковь Успения, Rua Nova da Igreja, Alte',
    },
  },
  team: {
    h1: 'Тимбилдинг в Алгарве: командные квесты-прогулки',
    lead: 'Превратите прогулку по городу в дружеское соревнование. Команды проходят один и тот же квест в Фаро, Оляо или Алте на своих телефонах и соревнуются по очкам и времени.',
    sections: [
      {
        title: 'Как это устроено',
        items: [
          'Группа делится на команды по 4–5 человек. Каждая команда играет со своего телефона.',
          'Все команды проходят один маршрут и решают загадки на каждой локации.',
          'Очки: 10 за верный ответ с первой попытки, 5 — если использовали подсказку, 0 — после двух ошибок.',
          'На финише сравниваются очки и время — побеждает команда с наибольшим счётом и лучшим временем.',
        ],
      },
      {
        title: 'Где',
        text: 'На выбор три маршрута: старый город Фаро, приморский Оляо или горная деревня Алте. Каждый занимает около 1,5 часа в спокойном темпе.',
      },
      {
        title: 'Что нужно участникам',
        items: [
          'Один заряженный телефон с мобильным интернетом на команду',
          'Удобная обувь — на маршрутах брусчатка и лестницы',
          'Дневной свет: некоторые подсказки основаны на мелких деталях',
        ],
      },
    ],
    orderTitle: 'Заказать мероприятие',
    orderText: 'Напишите нам — подтвердим условия для вашей группы. Укажите:',
    orderItems: ['Город или маршрут', 'Примерную дату', 'Количество участников'],
    whatsappCta: 'Написать в WhatsApp',
    emailCta: 'Написать на почту',
    whatsappText: 'Здравствуйте! Хотим организовать тимбилдинг-квест.',
    emailSubject: 'Тимбилдинг-квест',
    routesTitle: 'Маршруты для команд',
    invoiceBefore: 'Нужен счёт на компанию? Напишите нам в ',
    invoiceLink: 'WhatsApp',
    invoiceAfter: ', и мы его выставим.',
    invoiceWhatsappText: 'Здравствуйте! Нужен счёт на компанию для тимбилдинга.',
  },
};

export const COPY: Record<Lang, Copy> = { en, pt, ru };
